// Перевод между объектами. Одно действие, а не два: сервер закрывает прежнее назначение и
// создаёт новое одной операцией. Ручная последовательность «закрыть, потом поставить» дала бы
// дыру в один день посередине — и человек выпал бы из табеля именно за этот день.
//
// Тариф на новом объекте сервер переносит с прежнего. Хотите другой — после перевода
// действие «Изменить тариф» с датой и основанием: тариф меняется отдельным протоколом, чтобы
// в истории было видно, когда и почему он поехал.

import type { FormEvent } from 'react';
import { useState } from 'react';

import { RiCheckboxCircleLine, RiErrorWarningLine } from '@remixicon/react';

import type { ApiError } from '@/shared/errors/api-error';
import type { AssignmentOut, TransferIn } from '@/shared/api/endpoints/people';
import { useAuthStore } from '@/shared/auth/store';
import { useLegalEntitiesQuery, usePostsQuery, useSitesQuery } from '@/shared/org/queries';
import { can } from '@/shared/rbac/permissions';
import { formatDateOnly } from '@/shared/time/format';
import * as Alert from '@/shared/ui/alert';
import * as Button from '@/shared/ui/button';
import { DateField, todayIso } from '@/shared/ui/date-field';
import { ErrorAlert } from '@/shared/ui/error-alert';
import * as Hint from '@/shared/ui/hint';
import { NO_VALUE, SelectField } from '@/shared/ui/select-field';
import { TextField } from '@/shared/ui/text-field';

import { StaffingNotice, WarningsNotice } from '../components/AssignmentResult';
import { newIdempotencyKey, useTransferMutation } from '../queries';

const EDIT_PERMISSION = 'core.employee.edit';

type FieldName = 'site' | 'post' | 'date_from';

export function TransferPanel({ id, onClose }: { id: string; onClose: () => void }) {
  const permissions = useAuthStore((state) => state.permissions);
  const allowed = can(permissions, EDIT_PERMISSION);

  const [idempotencyKey] = useState(newIdempotencyKey);
  const [siteId, setSiteId] = useState(NO_VALUE);
  const [postId, setPostId] = useState(NO_VALUE);
  const [legalEntityId, setLegalEntityId] = useState(NO_VALUE);
  const [dateFrom, setDateFrom] = useState(todayIso);
  const [basis, setBasis] = useState('');
  const [errors, setErrors] = useState<Partial<Record<FieldName, string>>>({});
  const [gateNote, setGateNote] = useState<string | null>(null);
  const [done, setDone] = useState<AssignmentOut | null>(null);

  const sites = useSitesQuery({ active: true });
  const posts = usePostsQuery(siteId === NO_VALUE ? undefined : siteId);
  const legalEntities = useLegalEntitiesQuery();
  const mutation = useTransferMutation(id, idempotencyKey);

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!allowed) {
      setGateNote(
        'Переводить между объектами может тот, у кого есть право на правку карточек. Попросите администратора выдать его.',
      );
      return;
    }
    if (mutation.isPending) {
      setGateNote('Перевод уже выполняется, ждём ответ сервера. Второй запрос не отправляем.');
      return;
    }

    const next: Partial<Record<FieldName, string>> = {};
    if (siteId === NO_VALUE) {
      next.site = 'Выберите объект, на который переводим.';
    }
    if (postId === NO_VALUE) {
      next.post = 'Выберите пост нового объекта.';
    }
    if (dateFrom === '') {
      next.date_from = 'Укажите день, с которого действует новое назначение.';
    }
    if (Object.keys(next).length > 0) {
      setErrors(next);
      return;
    }

    setErrors({});
    setGateNote(null);
    const body: TransferIn = {
      post_id: postId,
      date_from: dateFrom,
      legal_entity_id: legalEntityId === NO_VALUE ? null : legalEntityId,
      basis: basis.trim() === '' ? null : basis.trim(),
    };
    mutation.mutate(body, { onSuccess: setDone });
  }

  if (done !== null) {
    return (
      <div className='flex flex-col gap-3'>
        <Alert.Root variant='light' status='success' size='small' role='status'>
          <Alert.Icon as={RiCheckboxCircleLine} />
          <span>
            Человек переведён на «{done.site_name} · {done.post_name}» с{' '}
            {formatDateOnly(done.date_from)}: {done.employee_name}. Прежнее назначение закрыто
            предыдущим днём.
          </span>
        </Alert.Root>
        <WarningsNotice warnings={done.warnings ?? []} />
        <StaffingNotice staffing={done.staffing ?? null} siteName={done.site_name} />
        <Button.Root type='button' variant='primary' mode='filled' size='small' onClick={onClose}>
          Готово
        </Button.Root>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate className='flex flex-col gap-3'>
      {!allowed && (
        <Alert.Root variant='light' status='warning' size='small' role='status'>
          <Alert.Icon as={RiErrorWarningLine} />
          <span>
            Права на правку карточек у вас нет: перевод не сохранится. Право выдаёт администратор.
          </span>
        </Alert.Root>
      )}

      <SelectField
        id='transfer-site'
        label='Новый объект'
        required
        value={siteId}
        onChange={(value) => {
          setSiteId(value);
          setPostId(NO_VALUE);
        }}
        options={(sites.data?.items ?? []).map((site) => ({ value: site.id, label: site.name }))}
        placeholder='Выберите объект'
        pending={sites.isPending}
        failed={sites.isError}
        failedError={sites.error as ApiError | null}
        emptyNote='Действующих объектов нет: переводить некуда.'
        error={errors.site}
      />

      <SelectField
        id='transfer-post'
        label='Пост'
        required
        value={postId}
        onChange={setPostId}
        options={(posts.data?.items ?? []).map((post) => ({ value: post.id, label: post.name }))}
        placeholder={siteId === NO_VALUE ? 'Сначала выберите объект' : 'Выберите пост'}
        pending={siteId !== NO_VALUE && posts.isPending}
        failed={posts.isError}
        failedError={posts.error as ApiError | null}
        emptyNote='У объекта нет ни одного поста: заведите пост в карточке объекта.'
        error={errors.post}
      />

      <SelectField
        id='transfer-legal-entity'
        label='Юрлицо'
        value={legalEntityId}
        onChange={setLegalEntityId}
        options={(legalEntities.data?.items ?? []).map((item) => ({
          value: item.id,
          label: item.short_name ?? item.name,
        }))}
        noneLabel='Оставить прежнее'
        placeholder='Оставить прежнее'
        pending={legalEntities.isPending}
        failed={legalEntities.isError}
        failedError={legalEntities.error as ApiError | null}
        emptyNote='Юридических лиц в справочнике нет. Их заводит администратор в настройках — юрлицо останется прежним.'
        hint='Меняйте, только если объект относится к другому юрлицу: от него зависит ведомость.'
      />

      <DateField
        id='transfer-date-from'
        label='Действует с'
        required
        value={dateFrom}
        onChange={setDateFrom}
        error={errors.date_from}
        hint='Прежнее назначение закроется предыдущим днём — дырки в табеле не будет.'
      />

      <TextField
        id='transfer-basis'
        label='Основание'
        value={basis}
        onChange={setBasis}
        placeholder='Приказ, просьба заказчика'
        hint='Необязательно, но объясняет через полгода, почему человек сменил объект.'
      />

      {mutation.isError && <ErrorAlert error={mutation.error} />}

      {gateNote !== null && (
        <Hint.Root hasError>
          <Hint.Icon as={RiErrorWarningLine} />
          {gateNote}
        </Hint.Root>
      )}

      <div className='flex items-center gap-2 pt-1'>
        <Button.Root type='submit' variant='primary' mode='filled' size='small'>
          {mutation.isPending ? 'Переводим…' : 'Перевести'}
        </Button.Root>
        <Button.Root type='button' variant='neutral' mode='stroke' size='small' onClick={onClose}>
          Отмена
        </Button.Root>
      </div>
    </form>
  );
}
